import {
  Cell,
  Field,
  HeaderFooterType,
  ImageStyle,
  MoveSectionPayload,
  PageOrientationType,
  PageSize,
  PageType,
  Row,
  Section,
  SelectedElement,
  SelectedElementWithIndex,
  SharedCellStyle,
  TableCell,
  TableField,
  TableRow,
  TargetType,
  TemplateState,
  TitleField,
} from '@components/pdf/react-pdf/react-pdf.interface';
import {
  cellStylePreset,
  getFieldCell,
  getFieldRow,
  getTableCellValue,
  getTableField,
  imageStylePreset,
  isSameElement,
  pageStylePreset,
  rowStylePreset,
  sectionStylePreset,
  selectSections,
  tableColumnsStylePreset,
  tableHeadersStylePreset,
  tableStylePreset,
  textStylePreset,
} from '@components/pdf/react-pdf/utils/react-pdf.helper';
import {
  createSlice,
  //  current,
  PayloadAction,
} from '@reduxjs/toolkit';

// #region INTERFACE & INITIAL STATE
export type HistoryState<T> = {
  past: T[];
  present: T;
  future: T[];
};

export const templateInitialState: TemplateState = {
  pages: [
    {
      sections: [],
      page: 1,
      height: 0,
      style: pageStylePreset,
      pageSize: { name: 'A4', width: 210, height: 297 },
      orientation: 'portrait',
    },
  ],
  selectedElement: undefined,
  config: {
    hideIdTagging: false,
    enableDesigner: true,
  },
  tagging: {
    image: [],
    table: [],
    textArea: [],
    title: [],
    layout: [],
  },
  selectedElements: [],
  selectedElementType: undefined,
};

const initialState: HistoryState<TemplateState> = {
  past: [],
  present: templateInitialState,
  future: [],
};

type TableCellUpdatePayload =
  | {
      type: 'id';
      value: string | null;
    }
  | {
      type: 'value';
      value: string;
    }
  | {
      type: 'colSpan';
      value: number | undefined;
    }
  | {
      type: 'rowSpan';
      value: number | undefined;
    }
  | {
      type: 'style';
      value: SharedCellStyle | undefined;
    };
// #endregion

// #region HELPER FUNCTIONS
const reorder = <T>(list: T[], from: number, to: number): T[] => {
  const result = [...list];
  const [moved] = result.splice(from, 1);
  result.splice(to, 0, moved);
  return result;
};
// #endregion

export const TemplateSlice = createSlice({
  name: 'template',
  initialState: templateInitialState,
  reducers: {
    resetTemplate: (
      _state,
      _action: PayloadAction<{ disregardChanges?: boolean }>,
    ) => templateInitialState,
    setTemplate: (
      _state,
      action: PayloadAction<TemplateState & { initialMount?: boolean }>,
    ) => {
      return action.payload;
    },
    setTemplateConfig: (
      state,
      action: PayloadAction<{
        hideIdTagging?: boolean;
        enableDesigner?: boolean;
      }>,
    ) => {
      state.config = {
        ...state.config,
        ...(action.payload.hideIdTagging !== undefined && {
          hideIdTagging: action.payload.hideIdTagging,
        }),
        ...(action.payload.enableDesigner !== undefined && {
          enableDesigner: action.payload.enableDesigner,
        }),
      };
    },

    // HEADER FOOTER
    moveSectionToHeaderOrFooter: (
      state,
      action: PayloadAction<MoveSectionPayload & { initialMount: boolean }>,
    ) => {
      const { pageIndex, sectionIndex, target, location } = action.payload;

      let section: HeaderFooterType | undefined;

      // 1. Try to find in page.sections
      const page = state.pages[pageIndex];
      if (page?.sections) {
        const pageSectionIndex = page.sections.findIndex(
          (item) => item.index === sectionIndex,
        );
        if (pageSectionIndex !== -1) {
          section = page.sections[pageSectionIndex] as HeaderFooterType;
          page.sections.splice(pageSectionIndex, 1);
        }
      }

      // 2. Try to find in existing header
      if (!section && state.header) {
        const headerIndex = state.header.findIndex(
          (item) => item.index === sectionIndex,
        );
        if (headerIndex !== -1) {
          section = state.header[headerIndex];
          state.header.splice(headerIndex, 1);
        }
      }

      // 3. Try to find in existing footer
      if (!section && state.footer) {
        const footerIndex = state.footer.findIndex(
          (item) => item.index === sectionIndex,
        );
        if (footerIndex !== -1) {
          section = state.footer[footerIndex];
          state.footer.splice(footerIndex, 1);
        }
      }

      // If section wasn't found in any source, abort
      if (!section) return;

      // 4. Clean old flags before applying new ones
      const { showAllPage, firstPageOnly, lastPageOnly, ...cleanedSection } =
        section;

      const enhancedSection: HeaderFooterType = {
        ...cleanedSection,
        showAllPage: location === 'all-page',
        firstPageOnly: location === 'first-page',
        lastPageOnly: location === 'last-page',
        target,
      };

      // 5. Move to the correct new target
      if (target === 'header') {
        state.header = [...(state.header ?? []), enhancedSection];
      } else {
        state.footer = [...(state.footer ?? []), enhancedSection];
      }

      // 6. Clear selection to avoid invalid selectedElement
      state.selectedElement = undefined;
    },
    moveSectionBackToPages: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        target: TargetType;
      }>,
    ) => {
      const { pageIndex, sectionIndex, target } = action.payload;

      let section: HeaderFooterType | undefined;

      // 1. Remove from header/footer based on target
      if (target === 'header' && state.header) {
        const headerIndex = state.header.findIndex(
          (item) => item.index === sectionIndex,
        );
        if (headerIndex !== -1) {
          section = state.header[headerIndex];
          state.header.splice(headerIndex, 1);
        }
      } else if (target === 'footer' && state.footer) {
        const footerIndex = state.footer.findIndex(
          (item) => item.index === sectionIndex,
        );
        if (footerIndex !== -1) {
          section = state.footer[footerIndex];
          state.footer.splice(footerIndex, 1);
        }
      }

      if (!section) return;

      // 2. Remove header/footer-specific flags before adding back to page
      const {
        showAllPage,
        firstPageOnly,
        lastPageOnly,
        target: _,
        ...cleanedSection
      } = section;

      const restoredSection: Section = {
        ...cleanedSection,
      };

      // 3. Add to the specified page
      const page = state.pages[pageIndex];
      if (!page) return;

      if (target === 'header') {
        page.sections = [restoredSection, ...page.sections];
      } else {
        page.sections = [...page.sections, restoredSection];
      }

      // 4. Clear selectedElement if it's invalid after this move
      if (
        state.selectedElement &&
        state.selectedElement.type === 'section' &&
        state.selectedElement.sectionIndex === sectionIndex &&
        state.selectedElement.target === target
      ) {
        state.selectedElement = undefined;
      }
    },

    // PAGES
    addPage(state, action: PayloadAction<PageType>) {
      state.pages.push({ ...action.payload, style: pageStylePreset });
    },
    deletePage(state, action: PayloadAction<{ pageIndex: number }>) {
      state.pages.splice(action.payload.pageIndex, 1);
    },
    updatePage(
      state,
      action: PayloadAction<{ pageIndex: number; data: Partial<PageType> }>,
    ) {
      Object.assign(state.pages[action.payload.pageIndex], action.payload.data);
    },
    updatePageHeight(state, action) {
      const { pageIndex, height } = action.payload;
      if (!state.pages[pageIndex]) return;
      state.pages[pageIndex].height = height;
    },
    updatePageSize(
      state,
      action: PayloadAction<{
        pageIndex: number;
        pageSize: PageSize;
        height?: number;
      }>,
    ) {
      const page = state.pages[action.payload.pageIndex];
      if (page) {
        page.pageSize = action.payload.pageSize;
        if (action.payload.height !== undefined) {
          page.height = action.payload.height;
        }
      }
    },
    updatePageOrientation(
      state,
      action: PayloadAction<{
        pageIndex: number;
        orientation: PageOrientationType;
      }>,
    ) {
      const { pageIndex, orientation } = action.payload;
      const page = state.pages[pageIndex];
      if (page) {
        page.orientation = orientation;
      }
    },
    updatePageBackgroundImage(
      state,
      action: PayloadAction<{
        pageIndex: number;
        backgroundImage: {
          value: string;
          style?: ImageStyle;
        };
        initialMount: boolean;
      }>,
    ) {
      const { pageIndex, backgroundImage } = action.payload;
      const page = state.pages[pageIndex];

      if (page) {
        page.backgroundImage = backgroundImage;
      }
    },

    // SECTIONS
    addSection(
      state,
      action: PayloadAction<{ pageIndex: number; title: string }>,
    ) {
      const { pageIndex, title } = action.payload;
      const newSection: Section = {
        index: Date.now(),
        title,
        rows: [],
        style: sectionStylePreset,
      };

      if (!state.pages[pageIndex]) {
        state.pages[pageIndex] = {
          page: pageIndex,
          sections: [],
          height: 0,
          pageSize: { name: 'A4', width: 210, height: 297 },
          orientation: 'portrait',
        };
      }

      state.pages[pageIndex].sections.push(newSection);
    },
    deleteSection(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        target?: TargetType;
      }>,
    ) {
      const { pageIndex, sectionIndex } = action.payload;
      const selected = state.selectedElement;

      if (
        !selected ||
        selected.type !== 'section' ||
        selected.pageIndex !== pageIndex ||
        selected.sectionIndex !== sectionIndex
      ) {
        return;
      }

      // Handle header/footer/body sections
      if (selected.target === 'header') {
        state?.header?.splice(sectionIndex, 1);
      } else if (selected.target === 'footer') {
        state?.footer?.splice(sectionIndex, 1);
      } else {
        state.pages[pageIndex].sections.splice(sectionIndex, 1);
      }

      // Clear selection
      state.selectedElement = undefined;
    },

    // ROWS
    addRow(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        target?: TargetType;
      }>,
    ) {
      const index = action.payload;
      const now = Date.now();
      const row: Row = {
        index: now,
        cells: [
          { index: now, style: cellStylePreset, fields: [] },
          { index: now + 1, style: cellStylePreset, fields: [] },
        ],
        style: rowStylePreset,
      };

      const section = selectSections(state, index);
      section?.rows.push(row);
    },
    addRowToCell(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        target?: TargetType;
      }>,
    ) {
      const { rowIndex, cellIndex, target, pageIndex, sectionIndex } =
        action.payload;
      const section = selectSections(state, {
        target,
        pageIndex,
        sectionIndex,
      });
      const cell = section?.rows?.[rowIndex]?.cells?.[cellIndex];
      if (!cell) return;
      if (!cell.rows) cell.rows = [];
      const now = Date.now();
      cell.rows.push({
        index: now,
        cells: [
          {
            index: now,
            style: {
              ...cellStylePreset,
            },
          },
          {
            index: now + 1,
            style: {
              ...cellStylePreset,
            },
          },
        ],
        style: rowStylePreset,
      });
    },
    deleteRow(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        cellIndex?: number;
        nestedRowIndex?: number;
        target?: TargetType;
      }>,
    ) {
      const {
        rowIndex,
        nestingLevel,
        parentIndex,
        cellIndex,
        nestedRowIndex,
        target,
        pageIndex,
        sectionIndex,
      } = action.payload;

      const section = selectSections(state, {
        target,
        pageIndex,
        sectionIndex,
      });

      if (
        nestingLevel > 0 &&
        parentIndex !== undefined &&
        cellIndex !== undefined &&
        nestedRowIndex !== undefined
      ) {
        const parentCell = section?.rows?.find((r) =>
          r.cells?.some((_, ci) => ci === parentIndex),
        )?.cells?.[parentIndex];

        if (!parentCell || !Array.isArray(parentCell.rows)) return;

        parentCell.rows.splice(nestedRowIndex, 1);
      } else {
        // Top-level row
        section?.rows.splice(rowIndex, 1);
      }

      state.selectedElement = undefined;
    },

    // CELLS
    addCellOld(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        target?: TargetType;
      }>,
    ) {
      const { rowIndex, pageIndex, sectionIndex, target } = action.payload;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });
      const row = section?.rows?.[rowIndex];
      if (!row) return;
      row.cells.push({
        index: Date.now(),
        style: cellStylePreset,
        fields: [],
      });
    },
    addCell: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        nestingLevel?: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        target?: TargetType;
      }>,
    ) => {
      const {
        pageIndex,
        sectionIndex,
        rowIndex,
        cellIndex,
        nestingLevel = 0,
        parentIndex,
        nestedRowIndex,
        target,
      } = action.payload;

      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });

      if (!section) return;

      /**
       * Resolve row
       */
      let row: Row | undefined;

      if (nestingLevel === 0) {
        row = section.rows?.[rowIndex];
      } else {
        const parentCell = section.rows?.[rowIndex]?.cells?.[parentIndex ?? -1];
        row = parentCell?.rows?.[nestedRowIndex ?? rowIndex];
      }

      if (!row) return;

      /**
       * Create new cell
       */
      const newCell: Cell = {
        index: Date.now(),
        style: cellStylePreset,
        fields: [],
      };

      /**
       * Insert beside target index
       */
      row.cells.splice(cellIndex + 1, 0, newCell);
    },
    deleteCell(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        target?: TargetType;
      }>,
    ) {
      const {
        rowIndex,
        cellIndex,
        nestingLevel,
        parentIndex,
        nestedRowIndex,
        pageIndex,
        sectionIndex,
        target,
      } = action.payload;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });

      if (nestingLevel && nestingLevel > 0 && parentIndex !== undefined) {
        const parentRow = section?.rows?.[rowIndex];
        if (!parentRow) return;

        const parentCell = parentRow.cells?.[parentIndex];
        if (!parentCell || !Array.isArray(parentCell.rows)) return;

        const nestedRow = parentCell.rows?.[nestedRowIndex ?? 0];
        if (!nestedRow) return;

        // Delete cell from nested row
        nestedRow.cells.splice(cellIndex, 1);

        if (nestedRow.cells.length === 0) {
          parentCell.rows.splice(nestedRowIndex ?? 0, 1);
          if (parentCell.rows.length === 0) {
            delete parentCell.rows;
          }
        }
      } else {
        // Top-level cell
        const row = section?.rows?.[rowIndex];
        if (!row) return;

        row.cells.splice(cellIndex, 1);

        if (row.cells.length === 0) {
          section.rows.splice(rowIndex, 1);
        }
      }

      state.selectedElement = undefined;
    },

    // FIELDS
    addFieldToCell: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        parentIndex?: number;
        nestingLevel: number;
        field: Field;
        nestedRowIndex?: number;
        target?: TargetType;
      }>,
    ) => {
      const {
        rowIndex,
        cellIndex,
        parentIndex,
        nestingLevel,
        field,
        nestedRowIndex,
        pageIndex,
        sectionIndex,
        target,
      } = action.payload;

      let cell: Cell | undefined;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });
      if (nestingLevel === 0) {
        cell = section?.rows[rowIndex].cells[cellIndex];
      } else {
        const parentCell = section?.rows[rowIndex].cells[parentIndex!];
        const nestedRow = parentCell?.rows?.[nestedRowIndex ?? rowIndex];
        cell = nestedRow?.cells?.[cellIndex];
      }

      if (!cell) return;
      if (!cell.fields) cell.fields = [];

      const now = Date.now();
      let fieldToAdd: Field;

      switch (field.type) {
        case 'title':
          fieldToAdd = {
            ...field,
            index: now,
            style: {
              ...textStylePreset,
              fontSize: 10,
              ...(field.style ?? {}),
            },
          };
          break;

        case 'textarea':
          fieldToAdd = {
            ...field,
            index: now,
            style: {
              ...textStylePreset,
              ...(field.style ?? {}),
            },
          };
          break;

        case 'image':
          fieldToAdd = {
            ...field,
            index: now,
            style: {
              ...imageStylePreset,
              ...(field.style ?? {}),
            },
          };
          break;

        case 'table':
          fieldToAdd = {
            ...field,
            index: now,
            type: 'table',
            rows: [
              {
                isHeader: true,
                cells: [{ value: 'Header 1' }, { value: 'Header 2' }],
              },
              {
                isHeader: false,
                cells: [{ value: 'Data 1' }, { value: 'Data 2' }],
              },
            ],
            style: tableStylePreset,
            headersStyle: tableHeadersStylePreset,
            columnsStyle: tableColumnsStylePreset,
            isShowHeader: true,
          };
          break;

        default:
          throw new Error(`Unknown field type: ${(field as any).type}`);
      }

      cell.fields.push(fieldToAdd);
    },
    deleteFieldFromCell: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        target?: TargetType;
      }>,
    ) => {
      const {
        rowIndex,
        cellIndex,
        fieldIndex,
        nestingLevel,
        parentIndex,
        nestedRowIndex,
        pageIndex,
        sectionIndex,
        target,
      } = action.payload;

      let cell: Cell | undefined;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });
      if (nestingLevel === 0) {
        cell = section?.rows[rowIndex].cells[cellIndex];
      } else {
        const parentCell = section?.rows[rowIndex].cells[parentIndex!];
        const nestedRow = parentCell?.rows?.[nestedRowIndex ?? rowIndex];
        cell = nestedRow?.cells?.[cellIndex];
      }

      if (!cell) return;

      cell.fields?.splice(fieldIndex, 1);

      state.selectedElement = undefined;
    },
    updateField: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        field: Partial<Field>;
        nestedRowIndex?: number;
        target?: TargetType;
      }>,
    ) => {
      const {
        rowIndex,
        cellIndex,
        fieldIndex,
        nestingLevel,
        parentIndex,
        field,
        nestedRowIndex,
        pageIndex,
        sectionIndex,
        target,
      } = action.payload;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });
      let cell: Cell | undefined;

      if (nestingLevel === 0) {
        cell = section?.rows[rowIndex].cells[cellIndex];
      } else {
        const parentCell = section?.rows[rowIndex].cells[parentIndex!];
        const nestedRow = parentCell?.rows?.[nestedRowIndex ?? rowIndex];
        cell = nestedRow?.cells?.[cellIndex];
      }

      const targetField = cell?.fields?.[fieldIndex];
      if (!targetField || targetField.type !== field.type) return;

      Object.assign(targetField, field);
    },
    updateFieldValue: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        value?: string;
        id?: string;
        nestedRowIndex?: number;
        target?: TargetType;
        isShowHeader?: boolean;
        isHideLastRowBorder?: boolean;
        initialMount: boolean;
      }>,
    ) => {
      const {
        rowIndex,
        cellIndex,
        fieldIndex,
        nestingLevel,
        parentIndex,
        value,
        id,
        nestedRowIndex,
        pageIndex,
        sectionIndex,
        target,
        isShowHeader,
        isHideLastRowBorder,
      } = action.payload;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });
      let cell: Cell | undefined;

      if (nestingLevel === 0) {
        cell = section?.rows[rowIndex].cells[cellIndex];
      } else {
        const parentCell = section?.rows[rowIndex].cells[parentIndex!];
        const nestedRow = parentCell?.rows?.[nestedRowIndex ?? rowIndex];
        cell = nestedRow?.cells?.[cellIndex];
      }

      const field = cell?.fields?.[fieldIndex];
      if (field) {
        if (typeof value !== 'undefined' && field.type !== 'table') {
          field.value = value;
        }
        if (typeof id !== 'undefined') {
          field.id = id;
        }
        if (typeof isShowHeader !== 'undefined' && field.type === 'table') {
          (field as TableField).isShowHeader = isShowHeader;
        }
        if (
          typeof isHideLastRowBorder !== 'undefined' &&
          field.type === 'table'
        ) {
          (field as TableField).isHideLastRowBorder = isHideLastRowBorder;
        }
      }
    },

    // SORTING
    reorderFieldsInCell(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fromIndex: number;
        toIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        target?: TargetType;
      }>,
    ) {
      const {
        rowIndex,
        cellIndex,
        fromIndex,
        toIndex,
        nestingLevel = 0,
        parentIndex,
        nestedRowIndex,
        pageIndex,
        sectionIndex,
        target,
      } = action.payload;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });
      let fields: Field[] | undefined;

      if (nestingLevel === 0) {
        // Top-level cell
        fields = section?.rows?.[rowIndex]?.cells?.[cellIndex]?.fields;
      } else {
        // Nested cell
        const parentCell =
          section?.rows?.[rowIndex]?.cells?.[parentIndex ?? -1];
        const nestedRow = parentCell?.rows?.[nestedRowIndex ?? rowIndex];
        const nestedCell = nestedRow?.cells?.[cellIndex];
        fields = nestedCell?.fields;
      }

      if (fields) {
        const [moved] = fields.splice(fromIndex, 1);
        fields.splice(toIndex, 0, moved);
      }

      state.selectedElement = undefined;
    },
    reorderSections: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        fromIndex: number;
        toIndex: number;
        target?: TargetType;
      }>,
    ) => {
      const { pageIndex, fromIndex, toIndex } = action.payload;
      const selected = state.selectedElement;

      if (!selected || selected.type !== 'section') return;

      if (selected.target === 'header') {
        if (!state.header) return;
        state.header = reorder(state.header, fromIndex, toIndex);
      } else if (selected.target === 'footer') {
        if (!state.footer) return;
        state.footer = reorder(state.footer, fromIndex, toIndex);
      } else {
        const page = state.pages[pageIndex];
        if (!page || !page.sections) return;
        page.sections = reorder(page.sections, fromIndex, toIndex);
      }

      state.selectedElement = undefined;
    },
    reorderRowsInSection: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        fromIndex: number;
        toIndex: number;
        target?: TargetType;
      }>,
    ) => {
      const { fromIndex, toIndex, pageIndex, sectionIndex, target } =
        action.payload;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });
      if (section && section.rows) {
        section.rows = reorder(section.rows, fromIndex, toIndex);
      }
      if (state.selectedElement) {
        state.selectedElement = undefined;
      }
    },
    reorderCellsInRow: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        fromIndex: number;
        toIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        target?: TargetType;
      }>,
    ) => {
      const {
        rowIndex,
        fromIndex,
        toIndex,
        nestingLevel = 0,
        parentIndex,
        pageIndex,
        sectionIndex,
        target,
      } = action.payload;
      const section = selectSections(state, {
        pageIndex,
        sectionIndex,
        target,
      });

      let row: Row | undefined;

      if (nestingLevel === 0) {
        row = section?.rows?.[rowIndex];
      } else if (nestingLevel === 1) {
        const parentCell =
          section?.rows?.[rowIndex]?.cells?.[parentIndex ?? -1];
        row = parentCell?.rows?.[rowIndex];
      }

      if (row?.cells) {
        row.cells = reorder(row.cells, fromIndex, toIndex);
      }

      if (state.selectedElement) {
        state.selectedElement = undefined;
      }
    },

    // SELECTED
    selectElement(state, action: PayloadAction<SelectedElement>) {
      const next = action.payload;

      if (state.selectedElement && isSameElement(state.selectedElement, next)) {
        state.selectedElement = undefined;
        state.selectedElements = [];
        state.selectedElementType = undefined;

        return;
      }

      state.selectedElement = next;
      state.selectedElements = [next];

      if (next.type === 'field') {
        state.selectedElementType = next.fieldType;
      } else {
        state.selectedElementType = next.type;
      }
    },
    updateSelectedElementStyle(
      state,
      action: PayloadAction<
        Partial<
          | PageType['style']
          | Section['style']
          | Row['style']
          | Cell['style']
          | TitleField['style']
        > & { initialMount: boolean }
      >,
    ) {
      const selected = state.selectedElement;
      if (!selected) return;

      const page = state.pages[selected.pageIndex];
      if (!page) return;

      if (selected.type === 'page') {
        page.style = { ...(page.style ?? {}), ...action.payload };
        return;
      }

      const section = selectSections(state);
      if (!section) return;

      if (selected.type === 'section') {
        section.style = { ...(section.style ?? {}), ...action.payload };
      } else {
        const row = section.rows[selected.rowIndex];
        if (!row) return;

        if (selected.type === 'row') {
          row.style = { ...(row.style ?? {}), ...action.payload };
        } else {
          const cell = row.cells[selected.cellIndex];
          if (!cell) return;

          if (selected.type === 'cell') {
            cell.style = { ...(cell.style ?? {}), ...action.payload };
          } else if (selected.type === 'field') {
            const field = cell.fields?.[selected.fieldIndex];
            if (field?.type === 'title' || field?.type === 'textarea') {
              field.style = { ...(field.style ?? {}), ...action.payload };
            }
          }
        }
      }
    },
    clearSelectedElement(state) {
      if (state.selectedElement) {
        state.selectedElement = undefined;
        state.selectedElements = [];
      }
    },
    selectElements: (
      state,
      action: PayloadAction<SelectedElementWithIndex>,
    ) => {
      const next = action.payload;
      const list = state.selectedElements || [];

      if (list.length && list[0].type !== next.type) return;

      const index = list.findIndex((item) => item.index === next.index);

      if (index !== -1) {
        state.selectedElements.splice(index, 1);
        if (!list.length) {
          state.selectedElementType = undefined;
        }

        return;
      }

      if (!list.length) {
        if (next.type === 'field') {
          state.selectedElementType = next.fieldType;
        } else {
          state.selectedElementType = next.type;
        }
      }

      if (!state.selectedElements?.length) {
        state.selectedElements = [next];
        state.selectedElement = next;
        return;
      }

      state.selectedElements.push(next);
    },
    clearSelectedElements: (state) => {
      state.selectedElementType = undefined;
      state.selectedElements = [];
    },

    // STYLE
    updateSectionStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        style: Partial<Section['style']>;
        id?: string;
        initialMount: boolean;
      }>,
    ) {
      const { style, id } = action.payload;

      const elementsToUpdate =
        state.selectedElements.length > 1
          ? state.selectedElements
          : [action.payload];

      elementsToUpdate.forEach((el) => {
        const section = selectSections(
          state,
          {
            pageIndex: el.pageIndex,
            sectionIndex: el.sectionIndex,
            target: el.target,
          },
          true,
        );

        if (section) {
          section.style = { ...section.style, ...style };
          if (typeof id !== 'undefined') {
            section.hideable = id;
          }
        }
      });
    },
    updateRowStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex?: number;
        nestingLevel: number;
        style: Partial<Row['style']>;
        nestedRowIndex?: number;
        id?: string;
        initialMount: boolean;
      }>,
    ) {
      const { style, id } = action.payload;

      const elementsToUpdate =
        state.selectedElements.length > 1
          ? state.selectedElements
          : [action.payload];

      elementsToUpdate.forEach((el) => {
        const row = getFieldRow(state, el);
        if (!row) return;

        row.style = {
          ...row.style,
          ...style,
        };

        if (typeof id !== 'undefined') {
          row.hideable = id;
        }
      });
    },
    updateCellStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        style: Partial<Cell['style']>;
        nestedRowIndex?: number;
        id?: string;
        initialMount: boolean;
      }>,
    ) {
      const { style, id } = action.payload;

      const elementsToUpdate =
        state.selectedElements.length > 1
          ? state.selectedElements
          : [action.payload];

      elementsToUpdate.forEach((el) => {
        const cell = getFieldCell(state, el);
        if (!cell) return;

        cell.style = {
          ...cell.style,
          ...style,
        };

        if (typeof id !== 'undefined') {
          cell.hideable = id;
        }
      });
    },

    // TABLE
    updateFieldStyle: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        nestingLevel: number;
        parentIndex?: number;
        style: Partial<Field['style']>;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) => {
      const { style } = action.payload;

      const elementsToUpdate =
        state.selectedElements.length > 1
          ? state.selectedElements
          : [action.payload];

      elementsToUpdate.forEach((el) => {
        const cell = getFieldCell(state, el);
        const field = cell?.fields?.[el.fieldIndex];

        if (field && ['title', 'textarea', 'image'].includes(field.type)) {
          field.style = { ...field.style, ...style };
        }
      });
    },
    updateTableFieldStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        style: Partial<TableField['style']>;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const field = getTableField(state, action.payload);
      if (field?.type === 'table') {
        field.style = { ...field.style, ...action.payload.style };
      }
    },
    updateTableHeadersStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        style: Partial<SharedCellStyle>;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const field = getTableField(state, action.payload);
      if (field?.type === 'table') {
        field.headersStyle = { ...field.headersStyle, ...action.payload.style };
      }
    },
    updateTableCellStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        style: Partial<SharedCellStyle>;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const field = getTableField(state, action.payload);
      if (field?.type === 'table') {
        field.columnsStyle = { ...field.columnsStyle, ...action.payload.style };
      }
    },
    updateTableCellAt(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        tableRowIndex: number;
        cellIndexInRow: number;
        style: Partial<SharedCellStyle>;
        value?: string;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
      }>,
    ) {
      const field = getTableField(state, action.payload);
      if (
        field?.type === 'table' &&
        field.rows?.[action.payload.tableRowIndex]
      ) {
        const tableRow = field.rows[action.payload.tableRowIndex];
        const targetCell = tableRow.cells?.[action.payload.cellIndexInRow];
        if (targetCell) {
          targetCell.style = {
            ...targetCell.style,
            ...action.payload.style,
          };
          if (action.payload.value !== undefined) {
            targetCell.value = action.payload.value;
          }
        }
      }
    },
    updateTableHeaderValue(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        columnIndex: number;
        value: string;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const { value, columnIndex } = action.payload;
      const field = getTableField(state, action.payload);

      if (field?.type === 'table' && field.rows) {
        const headerRow = field.rows.find((row) => row.isHeader);
        if (!headerRow) return;

        const cell = headerRow.cells?.[columnIndex];
        if (!cell) return;

        cell.value = value;
      }
    },
    updateTableDimensions(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        rowCount: number;
        columnCount: number;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const { rowCount, columnCount } = action.payload;
      const field = getTableField(state, action.payload);

      if (!field || field.type !== 'table') return;

      const existingRows = field.rows ?? [];
      const headerRow = existingRows.find((row) => row.isHeader) ?? {
        isHeader: true,
        cells: [],
      };

      // Update header cells
      const newHeaderCells: TableCell[] = Array.from({
        length: columnCount,
      }).map((_, colIndex) => ({
        value: headerRow.cells?.[colIndex]?.value ?? `Header ${colIndex + 1}`,
        colSpan: 1,
        rowSpan: 1,
        style: headerRow.cells?.[colIndex]?.style,
      }));

      // Update data rows
      const dataRows: TableRow[] = Array.from({ length: rowCount }).map(
        (_, rowIdx) => {
          const existingRow = existingRows.filter((r) => !r.isHeader)[rowIdx];
          return {
            isHeader: false,
            cells: Array.from({ length: columnCount }).map((_, colIdx) => ({
              value: existingRow?.cells?.[colIdx]?.value ?? '',
              colSpan: existingRow?.cells?.[colIdx]?.colSpan ?? 1,
              rowSpan: existingRow?.cells?.[colIdx]?.rowSpan ?? 1,
              id: existingRow?.cells?.[colIdx]?.id ?? undefined,
              style: existingRow?.cells?.[colIdx]?.style,
            })),
          };
        },
      );

      field.rows = [{ isHeader: true, cells: newHeaderCells }, ...dataRows];
    },
    updateTableCellValue: (
      state,
      action: PayloadAction<
        SelectedElement & {
          tableRowIndex: number;
          columnIndex: number;
          rowIndex: number;
          cellIndex: number;
          fieldIndex: number;
          sectionIndex: number;
          nestingLevel: number;
          parentIndex?: number;
          nestedRowIndex?: number;
          data: TableCellUpdatePayload;
          isDynamicData?: boolean;
          initialMount: boolean;
        }
      >,
    ) => {
      const {
        data: { type, value },
        isDynamicData,
        ...locator
      } = action.payload;

      const { cell, row } = getTableCellValue(state, locator);

      if (!cell) return;

      switch (type) {
        case 'id':
          cell.id = value;

          if (row) {
            row.isDynamicData = isDynamicData;
          }
          break;

        case 'value':
          cell.value = value;
          break;

        case 'colSpan':
          cell.colSpan = value;
          break;

        case 'rowSpan':
          cell.rowSpan = value;
          break;

        case 'style':
          cell.style = value;
          break;
      }
    },
    updateTableAlternateBackground(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        isAlternateBackground: boolean;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const field = getTableField(state, action.payload);

      if (field?.type === 'table') {
        field.isAlternateBackground = action.payload.isAlternateBackground;
      }
    },
    updateTableHeaderStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        columnIndex: number;
        style: SharedCellStyle;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const field = getTableField(state, action.payload);
      if (field?.type !== 'table') return;

      if (!field.headerStyles) {
        field.headerStyles = {};
      }

      const columnIndex = action.payload.columnIndex;
      const newStyle = action.payload.style;
      const prevStyle = field.headerStyles[columnIndex] || {};
      const changed: Partial<SharedCellStyle> = {};

      for (const key in newStyle) {
        if (newStyle[key] !== prevStyle[key]) {
          changed[key] = newStyle[key];
        }
      }

      if (Object.keys(changed).length > 0) {
        field.headerStyles[columnIndex] = {
          ...prevStyle,
          ...changed,
        };

        const headerRow = field.rows?.find((r) => r.isHeader);
        if (headerRow && headerRow.cells[columnIndex]) {
          const cell = headerRow.cells[columnIndex];
          cell.style = {
            ...(cell.style || {}),
            ...changed,
          };
        }
      }
    },
    updateTableColumnStyle(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        columnIndex: number;
        style: SharedCellStyle;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const field = getTableField(state, action.payload);
      if (field?.type !== 'table') return;

      if (!field.columnStyles) {
        field.columnStyles = {};
      }

      const columnIndex = action.payload.columnIndex;
      const newStyle = action.payload.style;
      const prevStyle = field.columnStyles[columnIndex] || {};
      const changed: Partial<SharedCellStyle> = {};

      for (const key in newStyle) {
        if (newStyle[key] !== prevStyle[key]) {
          changed[key] = newStyle[key];
        }
      }

      if (Object.keys(changed).length > 0) {
        field.columnStyles[columnIndex] = {
          ...prevStyle,
          ...changed,
        };

        field.rows?.forEach((row) => {
          if (!row.isHeader && row.cells[columnIndex]) {
            const cell = row.cells[columnIndex];
            cell.style = {
              ...(cell.style || {}),
              ...changed,
            };
          }
        });
      }
    },
    updateTableColumnColSpan(
      state,
      action: PayloadAction<{
        pageIndex: number;
        sectionIndex: number;
        rowIndex: number;
        cellIndex: number;
        fieldIndex: number;
        columnIndex: number;
        colSpan?: number;
        nestingLevel: number;
        parentIndex?: number;
        nestedRowIndex?: number;
        initialMount: boolean;
      }>,
    ) {
      const field = getTableField(state, action.payload);
      if (field?.type !== 'table') return;

      const { columnIndex, colSpan } = action.payload;

      field.rows?.forEach((row) => {
        if (row.cells[columnIndex]) {
          row.cells[columnIndex].colSpan = Number(colSpan);
        }
      });
    },
  },
});

export const {
  resetTemplate,
  addPage,
  deletePage,
  updatePage,
  updatePageHeight,
  addSection,
  deleteSection,
  addRow,
  deleteRow,
  addFieldToCell,
  deleteFieldFromCell,
  updateField,
  reorderFieldsInCell,
  selectElement,
  updateSelectedElementStyle,
  clearSelectedElement,
  updateSectionStyle,
  updateRowStyle,
  updateCellStyle,
  updateFieldStyle,
  updateFieldValue,
  deleteCell,
  addRowToCell,
  updateTableFieldStyle,
  updateTableHeadersStyle,
  updateTableCellStyle,
  updateTableCellAt,
  updateTableColumnStyle,
  updateTableHeaderStyle,
  updateTableHeaderValue,
  updateTableDimensions,
  updateTableCellValue,
  updateTableAlternateBackground,
  updatePageSize,
  updatePageOrientation,
  reorderSections,
  reorderRowsInSection,
  reorderCellsInRow,
  updatePageBackgroundImage,
  setTemplate,
  moveSectionToHeaderOrFooter,
  moveSectionBackToPages,
  setTemplateConfig,
  updateTableColumnColSpan,
  addCell,
  selectElements,
  clearSelectedElements,
} = TemplateSlice.actions;

export const templateReducer = TemplateSlice.reducer;

// #region UNDO/REDO
export const templateHistoryReducer = (
  state = initialState,
  action: {
    type: string;
    payload: { initialMount: boolean; disregardChanges?: boolean };
  },
): HistoryState<TemplateState> => {
  const ignoredActions = new Set([
    'template/selectElement',
    'template/clearSelectedElement',
    'template/selectElements',
    'template/clearSelectedElements',
  ]);

  const isInitialMount = action?.payload?.initialMount;
  const disregardChanges = action?.payload?.disregardChanges;

  switch (action.type) {
    case 'template/clearHistory': {
      return {
        past: [],
        present: state.present,
        future: [],
      };
    }
    case 'template/undo': {
      if (!state.past.length) return state;

      const previous = state.past[state.past.length - 1];

      return {
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future],
      };
    }

    case 'template/redo': {
      if (!state.future.length) return state;

      const next = state.future[0];

      return {
        past: [...state.past, state.present],
        present: next,
        future: state.future.slice(1),
      };
    }

    default: {
      const newPresent = templateReducer(state.present, action);

      if (
        ignoredActions.has(action.type) ||
        isInitialMount ||
        disregardChanges
      ) {
        return {
          ...state,
          present: newPresent,
        };
      }

      if (newPresent === state.present) return state;

      const MAX_HISTORY = 50;

      const safePast = state.past ?? [];

      return {
        past: [...safePast.slice(-MAX_HISTORY), state.present],
        present: newPresent,
        future: [],
      };
    }
  }
};

export const clearTemplateHistory = () => ({
  type: 'template/clearHistory',
});
export const undoTemplate = () => ({ type: 'template/undo' });
export const redoTemplate = () => ({ type: 'template/redo' });
// #endregion
